import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import {
  SCHEDULER_PREFILL_TYPES,
  SCHEDULER_TYPES,
  customSchedulerConversionScript,
  listedSchedulerType,
  schedulerPlaceholder,
  schedulerUrlMismatchWarning,
} from "../../utils/schedulerTypes";

const SchedulerEmbedSettings = (props) => {
  const object = props.object;
  const object_type = props.object_type;
  const object_id = props.object_id;
  const options = object.options || {};
  const schedulerType = listedSchedulerType(
    options["scheduler_type"] || "calendly"
  );

  const isType = (...types) => types.includes(schedulerType);
  const [copied, setCopied] = useState(false);

  const copyConversionScript = () => {
    const write = (text) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return Promise.resolve(navigator.clipboard.writeText(text));
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return Promise.resolve();
    };

    write(customSchedulerConversionScript)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <VariantBuilderSetting
        object={object}
        setting_name="[options][scheduler_type]"
        setting_type="select"
        label="Scheduler"
        object_type={object_type}
        object_id={object_id}
        value={schedulerType}
        options={SCHEDULER_TYPES}
      />

      <VariantBuilderSetting
        object={object}
        label="Scheduler link"
        setting_name="[options][scheduler-url]"
        setting_type="text"
        object_type={object_type}
        object_id={object_id}
        placeholder={schedulerPlaceholder(schedulerType)}
        value={options["scheduler-url"] ? options["scheduler-url"] : ""}
        warning={
          !options["scheduler-url"]
            ? "Scheduler link required"
            : schedulerUrlMismatchWarning(
                schedulerType,
                options["scheduler-url"]
              )
        }
        note={
          schedulerType === "custom"
            ? "Paste the booking page URL to embed. Install the conversion script below to track bookings and run confirmation actions."
            : "Paste the booking page URL from your scheduler"
        }
      >
        {isType("calendly", "cal", "savvycal") && (
          <React.Fragment>
            <VariantBuilderSetting
              object={object}
              label="Hide event details"
              setting_name="[options][scheduler-hide-details]"
              setting_type="checkbox"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-hide-details"]
                  ? options["scheduler-hide-details"]
                  : "false"
              }
              condition={isType("calendly", "cal")}
            />

            <VariantBuilderSetting
              object={object}
              label="Hide cookie banner"
              setting_name="[options][scheduler-hide-gdpr]"
              setting_type="checkbox"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-hide-gdpr"]
                  ? options["scheduler-hide-gdpr"]
                  : "false"
              }
              condition={isType("calendly")}
            />

            <VariantBuilderSetting
              object={object}
              label="Hide landing page details"
              setting_name="[options][scheduler-hide-landing]"
              setting_type="checkbox"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-hide-landing"]
                  ? options["scheduler-hide-landing"]
                  : "false"
              }
              condition={isType("calendly")}
            />

            <VariantBuilderSetting
              object={object}
              label="Theme"
              setting_name="[options][scheduler-theme]"
              setting_type="dropdown"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-theme"] ? options["scheduler-theme"] : ""
              }
              options={[
                { value: "", text: "Default" },
                { value: "light", text: "Light" },
                { value: "dark", text: "Dark" },
                { value: "auto", text: "Auto" },
              ]}
              condition={isType("cal", "savvycal")}
            />

            <VariantBuilderSetting
              object={object}
              label="Calendar layout"
              setting_name="[options][scheduler-layout]"
              setting_type="dropdown"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-layout"] ? options["scheduler-layout"] : ""
              }
              options={[
                { value: "", text: "Default" },
                { value: "month_view", text: "Month" },
                { value: "week_view", text: "Week" },
                { value: "column_view", text: "Column" },
              ]}
              condition={isType("cal")}
            />

            <VariantBuilderSetting
              object={object}
              label="Hide avatar"
              setting_name="[options][scheduler-hide-avatar]"
              setting_type="checkbox"
              object_type={object_type}
              object_id={object_id}
              value={
                options["scheduler-hide-avatar"]
                  ? options["scheduler-hide-avatar"]
                  : "false"
              }
              condition={isType("savvycal")}
            />
          </React.Fragment>
        )}
      </VariantBuilderSetting>

      {isType("custom") && (
        <div className="col-xs-12">
          <div className="mb-[5px] text-xl text-black dark:text-white">
            Conversion script
          </div>
          <p className="text-lg text-black dark:text-white mb-3">
            Paste this in your scheduler's custom code settings to fire upon
            scheduling.
          </p>
          <textarea
            readOnly
            className="w-full p-3 bg-white dark:bg-slate-950 text-lg text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 outline-none ring-0 appearance-none font-mono"
            rows={10}
            value={customSchedulerConversionScript}
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={copyConversionScript}
            className="mt-2 border-[1px] border-solid py-2 px-5 rounded-lg text-lg cursor-pointer bg-slate-950 dark:bg-slate-700 border-slate-950 dark:border-slate-950 text-white"
          >
            <FontAwesomeIcon icon="copy" className="mr-2" />
            {copied ? "Copied" : "Copy script"}
          </button>
        </div>
      )}

      <VariantBuilderSetting
        object={object}
        label="Prefill contact fields"
        setting_name="[options][scheduler-prefill]"
        setting_type="checkbox"
        object_type={object_type}
        object_id={object_id}
        value={
          options["scheduler-prefill"] ? options["scheduler-prefill"] : "true"
        }
        tooltip="Pass the visitor's name, email, and phone from ConvertFlow into the scheduler iframe URL when the provider supports it."
        condition={isType(...SCHEDULER_PREFILL_TYPES)}
      />
    </React.Fragment>
  );
};

export default SchedulerEmbedSettings;
